<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <div>
          <h2>{{ role.name }}</h2>
          <div class="breadcrumb">
            {{ role.subfamily?.group?.name }} › {{ role.subfamily?.name }}
          </div>
        </div>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <div class="modal-body">
        <!-- Infos générales -->
        <section class="section">
          <h3 class="section-title">{{ $t('bo.occupationSkills.editRole.generalInfo') }}</h3>
          <div class="form-group">
            <label>{{ $t('bo.occupationSkills.editRole.labelName') }}</label>
            <input v-model="form.name" type="text" class="input" />
          </div>
          <div class="form-group">
            <label>{{ $t('bo.common.description') }}</label>
            <textarea v-model="form.description" rows="3" class="input" />
          </div>
          <div class="meta-row">
            <div v-if="role.aliases?.length" class="meta-block">
              <span class="meta-label">{{ $t('bo.occupationSkills.editRole.aliases') }}</span>
              <span v-for="a in role.aliases" :key="a" class="tag">{{ a }}</span>
            </div>
            <div v-if="role.seniority_levels?.length" class="meta-block">
              <span class="meta-label">{{ $t('bo.occupationSkills.editRole.seniority') }}</span>
              <span v-for="s in role.seniority_levels" :key="s.level" class="tag">{{ s.level }}</span>
            </div>
          </div>
          <div class="btn-row">
            <button @click="saveRole" :disabled="saving" class="btn-primary">
              {{ saving ? $t('bo.common.saving') : $t('bo.common.save') }}
            </button>
            <span v-if="saveSuccess" class="success-msg">{{ $t('bo.common.saved') }}</span>
          </div>
        </section>

        <!-- Skills liés -->
        <section class="section">
          <h3 class="section-title">{{ $t('bo.occupationSkills.editRole.linkedSkills', { count: localSkills.length }) }}</h3>

          <div v-if="localSkills.length === 0" class="empty-msg">{{ $t('bo.occupationSkills.editRole.noSkills') }}</div>
          <template v-else>
            <div v-for="group in groupedSkills" :key="group.key" class="skill-group">
              <div :class="['skill-group-label', `skill-group-label--${group.key}`]">
                {{ group.label }}
                <span class="skill-group-count">{{ group.skills.length }}</span>
              </div>
              <table class="skills-table">
                <thead>
                  <tr>
                    <th>{{ $t('bo.occupationSkills.editRole.colSkill') }}</th>
                    <th>{{ $t('bo.occupationSkills.editRole.colKind') }}</th>
                    <th>{{ $t('bo.occupationSkills.editRole.colScope') }}</th>
                    <th>{{ $t('bo.occupationSkills.editRole.colImportance') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in group.skills" :key="s.id">
                    <td class="col-skill-name">{{ s.name }}</td>
                    <td>
                      <span :class="['badge-kind', `badge-kind--${s.atom_kind || 'skill'}`]">
                        {{ s.atom_kind || 'skill' }}
                      </span>
                    </td>
                    <td>
                      <span class="scope-readonly">{{ s.skill_type || 'technical' }}</span>
                    </td>
                    <td>
                      <select v-model="s.platform_importance" class="select-inline" @change="updateSkill(s)">
                        <option value="essential">essential</option>
                        <option value="important">important</option>
                        <option value="nice_to_have">nice to have</option>
                      </select>
                    </td>
                    <td>
                      <button @click="removeSkill(s)" class="btn-remove">✕</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- Ajouter un skill -->
          <div class="add-skill-form">
            <h4>{{ $t('bo.occupationSkills.editRole.addSkillTitle') }}</h4>
            <div class="add-row">
              <div class="search-wrapper">
                <input
                  v-model="addForm.search"
                  type="text"
                  :placeholder="$t('bo.occupationSkills.editRole.searchSkillPlaceholder')"
                  class="input"
                  @input="searchSkills"
                />
                <div v-if="skillResults.length > 0" class="skill-results">
                  <div
                    v-for="sk in skillResults"
                    :key="sk.id"
                    :class="['skill-result-item', addForm.skill_id === sk.id ? 'selected' : '']"
                    @click="selectSkill(sk)"
                  >
                    <span class="result-name">{{ sk.name }}</span>
                    <span :class="['badge-kind', `badge-kind--${sk.atom_kind || 'skill'}`]">{{ sk.atom_kind || 'skill' }}</span>
                  </div>
                </div>
                <div v-if="addForm.skill_id" class="selected-kind">
                  <span :class="['badge-kind', `badge-kind--${addForm.atom_kind || 'skill'}`]">{{ addForm.atom_kind || 'skill' }}</span>
                </div>
              </div>
              <select v-model="addForm.platform_importance" class="filter-select">
                <option value="essential">essential</option>
                <option value="important">important</option>
                <option value="nice_to_have">nice to have</option>
              </select>
              <button @click="addSkill" :disabled="!addForm.skill_id || adding" class="btn-primary">
                {{ adding ? '...' : $t('bo.common.add') }}
              </button>
            </div>
            <div v-if="addError" class="error-inline">{{ addError }}</div>
          </div>
        </section>

        <!-- ESCO Skill Enrichment -->
        <section class="section section-esco">
          <h3 class="section-title section-title-esco">{{ $t('bo.occupationSkills.editRole.escoEnrichment') }}</h3>
          <p class="esco-hint">{{ $t('bo.occupationSkills.editRole.escoEnrichmentHint') }}</p>

          <div v-if="escoLoading" class="esco-state">{{ $t('bo.occupationSkills.editRole.escoEnrichmentLoading') }}</div>
          <div v-else-if="escoError" class="esco-error">{{ $t('bo.occupationSkills.editRole.escoEnrichmentFailed') }}</div>
          <div v-else-if="escoSuggestions.length === 0" class="esco-state esco-empty">
            {{ $t('bo.occupationSkills.editRole.escoEnrichmentEmpty') }}
          </div>
          <div v-else>
            <!-- Groupé par skill_type -->
            <div v-for="group in groupedSuggestions" :key="group.type" class="esco-group">
              <div class="esco-group-label">{{ group.type }}</div>
              <div class="esco-skills-grid">
                <div v-for="sk in group.skills" :key="sk.id" class="esco-skill-card">
                  <div class="esco-skill-info">
                    <span class="esco-skill-name">{{ sk.name }}</span>
                    <div class="esco-skill-meta">
                      <span :class="['badge-kind', `badge-kind--${sk.atom_kind || 'skill'}`]">{{ sk.atom_kind || 'skill' }}</span>
                      <span v-if="sk.esco_context?.esco_skill_title" class="esco-context-label">
                        {{ $t('bo.occupationSkills.editRole.escoSuggestionEscoContext', { title: sk.esco_context.esco_skill_title }) }}
                      </span>
                    </div>
                  </div>
                  <button
                    @click="addEscoSuggestion(sk)"
                    :disabled="isAlreadyLinked(sk.id) || sk._adding || sk._added"
                    class="btn-esco-add"
                    :class="{ 'btn-esco-added': sk._added }"
                  >
                    {{ sk._adding ? $t('bo.occupationSkills.editRole.escoEnrichmentAdding') : sk._added ? $t('bo.occupationSkills.editRole.escoEnrichmentAdded') : isAlreadyLinked(sk.id) ? '✓' : $t('bo.common.add') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ESCO sources (read-only) -->
        <section v-if="role.esco_sources?.length" class="section">
          <h3 class="section-title">{{ $t('bo.occupationSkills.editRole.escoSources') }}</h3>
          <div class="esco-sources">
            <span v-for="src in role.esco_sources" :key="src.label" class="tag tag-esco">
              {{ src.label }}
            </span>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import occupationSkillService from '@/services/occupationSkillService'
import api from '@/services/api'

const { t } = useI18n()

const props = defineProps({ role: { type: Object, required: true } })
const emit = defineEmits(['close', 'updated'])

const TOOLS_KINDS = ['tool', 'certification']

const form = reactive({ name: props.role.name || '', description: props.role.description || '' })
const localSkills = ref([...(props.role.skills || [])])
const saving = ref(false)
const saveSuccess = ref(false)
const adding = ref(false)
const addError = ref('')
const skillResults = ref([])
const addForm = reactive({
  search: '', skill_id: null, skill_name: '', atom_kind: null,
  platform_importance: 'essential',
})
let searchTimer = null

// ESCO enrichment
const escoSuggestions = ref([])
const escoLoading = ref(false)
const escoError = ref(false)

const isAlreadyLinked = (skillId) => localSkills.value.some(s => s.id === skillId || s.skill_id === skillId)

const groupedSkills = computed(() => {
  const technical = localSkills.value.filter(s => !TOOLS_KINDS.includes(s.atom_kind))
  const tools = localSkills.value.filter(s => TOOLS_KINDS.includes(s.atom_kind))
  return [
    { key: 'technical', label: 'Technical', skills: technical },
    { key: 'tools', label: 'Tools', skills: tools },
  ].filter(g => g.skills.length > 0)
})

const groupedSuggestions = computed(() => {
  const order = ['technical', 'transversal', 'language']
  const map = {}
  for (const sk of escoSuggestions.value) {
    const type = sk.skill_type || 'technical'
    if (!map[type]) map[type] = []
    map[type].push(sk)
  }
  return order.filter(t => map[t]).map(t => ({ type: t, skills: map[t] }))
})

const loadEscoSuggestions = async () => {
  escoLoading.value = true
  escoError.value = false
  try {
    const res = await occupationSkillService.getEscoSuggestions(props.role.id)
    escoSuggestions.value = (res.data || []).map(sk => ({ ...sk, _adding: false, _added: false }))
  } catch {
    escoError.value = true
  } finally {
    escoLoading.value = false
  }
}

const addEscoSuggestion = async (sk) => {
  if (isAlreadyLinked(sk.id) || sk._adding || sk._added) return
  sk._adding = true
  try {
    await occupationSkillService.addRoleSkill(props.role.id, {
      skill_id: sk.id,
      // Le scope est deduit du type du skill cote back (Platform Skills).
      platform_importance: 'important',
    })
    sk._added = true
    const res = await occupationSkillService.getRole(props.role.id)
    localSkills.value = res.data?.skills || []
  } catch {
    sk._adding = false
  }
}

const saveRole = async () => {
  saving.value = true
  saveSuccess.value = false
  try {
    await occupationSkillService.updateRole(props.role.id, { name: form.name, description: form.description })
    saveSuccess.value = true
    emit('updated')
  } catch (e) {
    alert(e.response?.data?.message || t('bo.common.failedSave'))
  } finally {
    saving.value = false
  }
}

const updateSkill = async (skill) => {
  try {
    // Le scope (type) n'est plus editable : seule l'importance est propre au role.
    await occupationSkillService.updateRoleSkill(props.role.id, skill.id, {
      platform_importance: skill.platform_importance,
    })
  } catch {
    alert(t('bo.occupationSkills.editRole.failedUpdateSkill'))
  }
}

const removeSkill = async (skill) => {
  if (!confirm(t('bo.occupationSkills.editRole.confirmRemoveSkill', { name: skill.name }))) return
  try {
    await occupationSkillService.removeRoleSkill(props.role.id, skill.id)
    localSkills.value = localSkills.value.filter(s => s.id !== skill.id)
  } catch {
    alert(t('bo.occupationSkills.editRole.failedRemoveSkill'))
  }
}

const searchSkills = () => {
  clearTimeout(searchTimer)
  if (!addForm.search || addForm.search.length < 2) { skillResults.value = []; return }
  searchTimer = setTimeout(async () => {
    try {
      const res = await api.get('/admin/platform-skills', { params: { search: addForm.search, per_page: 10, status: 'active' } })
      skillResults.value = res.data?.data || []
    } catch { skillResults.value = [] }
  }, 300)
}

const selectSkill = (sk) => {
  addForm.skill_id = sk.id
  addForm.skill_name = sk.name
  addForm.atom_kind = sk.atom_kind || 'skill'
  addForm.search = sk.name
  skillResults.value = []
}

const addSkill = async () => {
  if (!addForm.skill_id) return
  adding.value = true
  addError.value = ''
  try {
    await occupationSkillService.addRoleSkill(props.role.id, {
      skill_id: addForm.skill_id,
      platform_importance: addForm.platform_importance,
    })
    const res = await occupationSkillService.getRole(props.role.id)
    localSkills.value = res.data?.skills || []
    addForm.search = ''; addForm.skill_id = null; addForm.skill_name = ''; addForm.atom_kind = null
  } catch (e) {
    addError.value = e.response?.data?.message || t('bo.occupationSkills.editRole.failedAddSkill')
  } finally {
    adding.value = false
  }
}

onMounted(loadEscoSuggestions)
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 2rem 1rem; overflow: hidden;
}
.modal {
  background: white; border-radius: 0.75rem; width: 100%; max-width: 900px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  display: flex; flex-direction: column;
  max-height: calc(100vh - 4rem);
}

.modal-header {
  flex-shrink: 0;
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e7eb;
}
.modal-header h2 { font-size: 1.1rem; font-weight: 700; color: #1f2937; margin: 0 0 0.2rem; }
.breadcrumb { font-size: 0.8rem; color: #9ca3af; }
.btn-close { background: none; border: none; font-size: 1.1rem; color: #6b7280; cursor: pointer; padding: 0.25rem 0.5rem; }

.modal-body { flex: 1; overflow-y: auto; padding: 1.5rem; display: flex; flex-direction: column; gap: 2rem; }
.section { display: flex; flex-direction: column; gap: 0.875rem; }
.section-title {
  font-size: 0.8rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #374151;
  border-bottom: 1px solid #e5e7eb; padding-bottom: 0.5rem; margin: 0;
}
.section-title-esco { color: #6d28d9; border-bottom-color: #ede9fe; }

.form-group { display: flex; flex-direction: column; gap: 0.3rem; }
.form-group label { font-size: 0.8rem; font-weight: 600; color: #374151; }
.input { padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem; width: 100%; box-sizing: border-box; }
textarea.input { resize: vertical; font-family: inherit; }

.meta-row { display: flex; flex-wrap: wrap; gap: 1rem; }
.meta-block { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }
.meta-label { font-size: 0.75rem; font-weight: 600; color: #6b7280; }
.tag { font-size: 0.75rem; background: #f3f4f6; color: #374151; padding: 0.15rem 0.5rem; border-radius: 9999px; }
.tag-esco { background: #ede9fe; color: #6d28d9; }

.btn-row { display: flex; align-items: center; gap: 0.75rem; }
.btn-primary { padding: 0.5rem 1.25rem; background: #3b82f6; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.success-msg { font-size: 0.8rem; color: #059669; }

.empty-msg { padding: 1.25rem; text-align: center; color: #9ca3af; background: #f9fafb; border-radius: 0.5rem; font-size: 0.875rem; }

.skill-group { margin-bottom: 1rem; }
.skill-group-label {
  display: flex; align-items: center; gap: 0.5rem;
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; padding: 0.35rem 0.5rem;
  border-radius: 0.25rem; margin-bottom: 0.375rem;
}
.skill-group-label--technical { background: #dbeafe; color: #1d4ed8; }
.skill-group-label--tools { background: #fef3c7; color: #92400e; }
.skill-group-count {
  font-size: 0.65rem; font-weight: 600;
  background: rgba(0,0,0,0.1); border-radius: 9999px;
  padding: 0.1rem 0.4rem;
}

.skills-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.skills-table th { padding: 0.5rem 0.75rem; text-align: left; font-size: 0.75rem; font-weight: 600; color: #6b7280; background: #f9fafb; border-bottom: 1px solid #e5e7eb; }
.skills-table td { padding: 0.6rem 0.75rem; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
.col-skill-name { font-weight: 500; color: #1f2937; }

/* Badge atom_kind */
.badge-kind {
  display: inline-block;
  font-size: 0.65rem; font-weight: 700; text-transform: uppercase;
  padding: 0.15rem 0.45rem; border-radius: 9999px; letter-spacing: 0.04em;
}
.badge-kind--skill       { background: #d1fae5; color: #065f46; }
.badge-kind--tool        { background: #fef3c7; color: #92400e; }
.badge-kind--framework   { background: #dbeafe; color: #1d4ed8; }
.badge-kind--certification { background: #ede9fe; color: #6d28d9; }

.select-inline { padding: 0.25rem 0.5rem; border: 1px solid #d1d5db; border-radius: 0.25rem; font-size: 0.8rem; }
.scope-readonly { font-size: 0.8rem; color: #4b5563; text-transform: capitalize; }
.btn-remove { background: none; border: none; color: #ef4444; cursor: pointer; font-size: 0.85rem; padding: 0.2rem 0.4rem; }
.btn-remove:hover { color: #dc2626; }

.add-skill-form { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
.add-skill-form h4 { margin: 0; font-size: 0.8rem; font-weight: 700; color: #374151; }
.add-row { display: flex; gap: 0.5rem; align-items: flex-start; flex-wrap: wrap; }
.add-row .input { width: 220px; }

.search-wrapper { position: relative; }
.filter-select { padding: 0.5rem 0.75rem; border: 1px solid #d1d5db; border-radius: 0.375rem; font-size: 0.875rem; }
.skill-results { position: absolute; top: 100%; left: 0; right: 0; border: 1px solid #d1d5db; border-radius: 0.375rem; background: white; max-height: 180px; overflow-y: auto; z-index: 10; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.skill-result-item { padding: 0.5rem 0.75rem; font-size: 0.875rem; cursor: pointer; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.skill-result-item:hover, .skill-result-item.selected { background: #eff6ff; }
.result-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.selected-kind { margin-top: 0.35rem; }

.muted { color: #9ca3af; }
.error-inline { font-size: 0.8rem; color: #dc2626; }

/* ESCO Enrichment */
.section-esco { background: #faf5ff; border: 1px solid #ede9fe; border-radius: 0.5rem; padding: 1rem; }
.esco-hint { font-size: 0.8rem; color: #6b7280; margin: 0 0 1rem; }
.esco-state { padding: 1rem; text-align: center; color: #9ca3af; font-size: 0.875rem; }
.esco-empty { font-style: italic; }
.esco-error { padding: 0.75rem; background: #fef2f2; border: 1px solid #fecaca; border-radius: 0.375rem; color: #dc2626; font-size: 0.875rem; }

.esco-group { margin-bottom: 1rem; }
.esco-group-label {
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #6d28d9; margin-bottom: 0.5rem;
}
.esco-skills-grid { display: flex; flex-direction: column; gap: 0.4rem; }
.esco-skill-card {
  display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
  background: white; border: 1px solid #e9d5ff; border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
}
.esco-skill-info { display: flex; flex-direction: column; gap: 0.2rem; flex: 1; min-width: 0; }
.esco-skill-name { font-size: 0.875rem; font-weight: 600; color: #1f2937; }
.esco-skill-meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }
.esco-context-label { font-size: 0.7rem; color: #9ca3af; font-style: italic; }

.btn-esco-add {
  flex-shrink: 0;
  padding: 0.3rem 0.75rem; background: #7c3aed; color: white;
  border: none; border-radius: 0.375rem; font-size: 0.8rem; font-weight: 600;
  cursor: pointer; white-space: nowrap;
}
.btn-esco-add:hover:not(:disabled) { background: #6d28d9; }
.btn-esco-add:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-esco-add.btn-esco-added { background: #059669; }

.esco-sources { display: flex; flex-wrap: wrap; gap: 0.4rem; }
</style>
